/*
  A/B test "test-pdp-022" — removes the upsell property once its main product is gone.

  An upsell line (`_upsell_popup_022` = main product id) belongs next to its
  main product line (`_upsell_popup_main_022` = the same id). When that main
  line leaves the cart, the drawer or cart page re-renders with an
  <upsell-cleanup-022> element (snippets/upsell-cleanup-022.liquid) listing the
  orphaned upsell lines, and this swaps each one for the same variant and
  quantity without the property. Shopify cannot clear properties on an
  existing line, so the untagged copy is added first and the tagged line is
  removed after. The upsell product stays in the cart, at its normal price.

  Not gated on the test class: only test-group carts ever carry these properties.
*/

(() => {
  if (customElements.get('upsell-cleanup-022')) return;

  const UPSELL_PROPERTY = '_upsell_popup_022';
  const MAIN_PROPERTY = '_upsell_popup_main_022';

  // A swap that fails (e.g. sold out) must not retry on every re-render.
  const attempted = new Set();
  let running = false;
  let queued = false;

  const postJSON = (url, payload) =>
    fetch(url, { ...fetchConfig('javascript'), body: JSON.stringify(payload) }).then((response) =>
      response.text().then((text) => {
        const data = JSON.parse(text);
        return response.ok && !data.status ? { data, text } : Promise.reject(data);
      })
    );

  // Where this page shows the cart: the drawer everywhere, the cart page's own sections on /cart.
  const getRenderTargets = () => {
    const miniCart = document.querySelector('mini-cart');
    if (miniCart && typeof miniCart.getSectionsToRender === 'function') {
      return {
        drawer: true,
        sections: miniCart.getSectionsToRender().map((section) => ({ ...section, section: section.id })),
      };
    }

    const cartItems = document.querySelector('cart-items');
    if (cartItems && typeof cartItems.getSectionsToRender === 'function') {
      return {
        drawer: false,
        sections: cartItems.getSectionsToRender().filter((section) => section.section && document.getElementById(section.id)),
      };
    }

    return { drawer: false, sections: [] };
  };

  // Mirrors MiniCart.renderContents / CartItems.updateQuantity, without opening the drawer.
  const render = (parsedState, text, targets) => {
    targets.sections.forEach(({ id, section, selector }) => {
      const element = document.getElementById(id);
      const html = parsedState.sections && parsedState.sections[section];
      if (!element || !html) return;

      const source = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      if (!source) return;

      const target = targets.drawer ? element : element.querySelector(selector) || element;
      target.innerHTML = source.innerHTML;
    });

    const miniCart = document.querySelector('mini-cart');
    if (miniCart) {
      if (typeof miniCart.initializeMiniCartReviews === 'function') miniCart.initializeMiniCartReviews();
      if (typeof miniCart.initializeTimer === 'function') miniCart.initializeTimer();
    }

    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: text } }));
  };

  const swap = async (keys) => {
    const cart = await fetch(`${window.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then(
      (response) => response.json()
    );

    // Re-check against the live cart: the render that listed these keys may be stale.
    const mainIds = new Set(
      cart.items
        .map((item) => item.properties && item.properties[MAIN_PROPERTY])
        .filter(Boolean)
        .map(String)
    );
    const orphans = cart.items.filter((item) => {
      const mainId = item.properties && item.properties[UPSELL_PROPERTY];
      return keys.includes(item.key) && mainId && !mainIds.has(String(mainId));
    });
    if (!orphans.length) return;

    // Untagged copies first: if they cannot be added, the tagged lines stay as they are.
    await postJSON(window.routes.cart_add_url, {
      items: orphans.map((item) => {
        const line = { id: item.variant_id, quantity: item.quantity };

        const properties = { ...item.properties };
        delete properties[UPSELL_PROPERTY];
        if (Object.keys(properties).length) line.properties = properties;

        if (item.selling_plan_allocation) line.selling_plan = item.selling_plan_allocation.selling_plan.id;

        return line;
      }),
    });

    const targets = getRenderTargets();
    const updates = {};
    orphans.forEach((item) => {
      updates[item.key] = 0;
    });

    const { data, text } = await postJSON(window.routes.cart_update_url, {
      updates,
      sections: [...new Set(targets.sections.map((section) => section.section))],
      sections_url: window.location.pathname,
    });

    render(data, text, targets);
  };

  class UpsellCleanup022 extends HTMLElement {
    connectedCallback() {
      this.run();
    }

    run() {
      // One swap at a time; whatever is rendered once it finishes gets checked then.
      if (running) {
        queued = true;
        return;
      }

      const keys = (this.dataset.keys || '').split(',').filter((key) => key && !attempted.has(key));
      if (!keys.length) return;

      running = true;
      keys.forEach((key) => attempted.add(key));

      swap(keys)
        .catch((error) => console.error(error))
        .finally(() => {
          running = false;
          if (!queued) return;

          queued = false;
          document.querySelectorAll('upsell-cleanup-022').forEach((element) => element.run());
        });
    }
  }

  customElements.define('upsell-cleanup-022', UpsellCleanup022);
})();
